import { DependencyList, useEffect } from 'react'

type Key = string
type Callback = () => void

const lookup: Record<string, boolean> = {}

export function useHotkey(keyString: Key, callback: Callback, deps: DependencyList = []) {
  useEffect(() => {
    const parts = keyString.split('+')
    const keyCode = parts[parts.length - 1]
    const useCtrl = parts[0] === 'ctrl'
    const useShift = parts[0] === 'shift'

    // prevent double binding in development
    if (process.env.NODE_ENV !== 'production') {
      if (lookup[keyString]) {
        throw new Error('already a key bound to ' + keyString)
      }
      lookup[keyString] = true
    }

    const listener = (event: KeyboardEvent) => {
      // don't eat keyboard inside inputs
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }
      // don't eat keybaord inside tiptap
      if (event.target instanceof HTMLDivElement) {
        if (event.target.classList.contains('ProseMirror')) {
          return
        }
      }
      const isCtrlDown = event.ctrlKey || event.metaKey
      const isShiftDown = event.shiftKey
      const shiftCtrl = isCtrlDown && isShiftDown

      // allow refresh
      if ((isCtrlDown || shiftCtrl) && event.key === 'r') {
        return
      }

      // do nothing if ctrl (or cmd) key is required and not pressed
      if (useCtrl && !isCtrlDown) {
        return
      }
      if (useShift && !isShiftDown) {
        return
      }
      if (event.key === keyCode) {
        event.preventDefault()
        event.stopPropagation()
        callback()
      }
    }

    document.addEventListener('keydown', listener)

    return () => {
      if (process.env.NODE_ENV !== 'production') {
        lookup[keyString] = false
      }
      document.removeEventListener('keydown', listener)
    }
  }, [keyString, ...deps])
}
